// Data table (DESIGN_SYSTEM §9.2): sticky header, right-aligned ledger numbers, and a mobile card
// fallback chosen by a container query (< 640 px container), not the viewport. States: loading
// (5 skeleton rows after 300 ms, aria-busy), empty / no results, error with Retry, permission-denied.
// Virtualisation past 200 rows and cursor pagination arrive with the first long list (ORDER-001).
import type { ReactNode } from "react";
import { t } from "@ady/i18n";
import { cx, useDelayedFlag } from "../lib";
import type { DataState } from "../state";
import { Checkbox } from "./Choice";
import { Skeleton } from "./Skeleton";
import { ErrorState, StatePanel, type StatePanelProps } from "./StatePanel";

export interface Column<Row> {
  id: string;
  header: string;
  cell: (row: Row) => ReactNode;
  align?: "start" | "end";
  /** Money, quantities and counts use ledger figures. */
  numeric?: boolean;
  /** Lower numbers stay longer as space shrinks; columns with priority > 2 hide below lg. */
  priority?: number;
  /** Merchant content (names, titles): Devanagari line height. */
  userContent?: boolean;
}

export interface CardLayout<Row> {
  title: (row: Row) => ReactNode;
  status?: (row: Row) => ReactNode;
  amount?: (row: Row) => ReactNode;
  /** Folded secondary fields ("details line"). */
  details?: (row: Row) => ReactNode;
  extra?: (row: Row) => ReactNode;
}

export interface DataTableProps<Row> {
  caption: string;
  columns: Column<Row>[];
  rows: Row[];
  rowId: (row: Row) => string;
  card: CardLayout<Row>;
  state?: DataState;
  /** Shown for state="empty". */
  empty?: Omit<StatePanelProps, "kind" | "size">;
  onRetry?: () => void;
  requestId?: string;
  permission?: { title: string; body: string };
  selectable?: boolean;
  selected?: Set<string>;
  onSelectedChange?: (next: Set<string>) => void;
  onRowOpen?: (row: Row) => void;
  dense?: boolean;
  className?: string;
  "data-testid"?: string;
}

export function DataTable<Row>({
  caption,
  columns,
  rows,
  rowId,
  card,
  state = "ready",
  empty,
  onRetry,
  requestId,
  permission,
  selectable = false,
  selected = new Set(),
  onSelectedChange,
  onRowOpen,
  dense = false,
  className,
  "data-testid": testId,
}: DataTableProps<Row>) {
  const loading = state === "loading";
  const showSkeleton = useDelayedFlag(loading);
  if (state === "error")
    return <ErrorState onRetry={onRetry} requestId={requestId} data-testid={testId} />;
  if (state === "permission-denied")
    return (
      <StatePanel
        kind="permission-denied"
        title={permission?.title ?? t("states.permissionDeniedTitle")}
        body={
          permission?.body ??
          t("states.permissionDeniedBody", { who: t("states.permissionWhoOwner") })
        }
        data-testid={testId}
      />
    );
  if (state === "empty" && empty)
    return <StatePanel kind="empty" {...empty} data-testid={testId} />;

  const toggle = (id: string) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    onSelectedChange?.(next);
  };
  const allSelected = rows.length > 0 && rows.every((r) => selected.has(rowId(r)));
  const skeletonRows = Array.from({ length: 5 }, (_, i) => i);
  const hideClass = (c: Column<Row>) =>
    (c.priority ?? 1) > 2 ? "hidden lg:table-cell" : undefined;

  return (
    <div
      className={cx("ady-table-wrap", className)}
      data-testid={testId}
      data-state={state}
      aria-busy={loading || undefined}
    >
      {loading ? <span className="ady-sr-only">{t("table.loadingRows")}</span> : null}

      {/* Wide container: a real table. */}
      <div className="ady-table-grid overflow-x-auto rounded-xs border border-subtle bg-surface">
        <table className="w-full border-collapse text-start">
          <caption className="ady-sr-only">{caption}</caption>
          <thead>
            {/* D-038: compact uppercase headers on the surface, a hairline under the header row. */}
            <tr className="border-b border-subtle bg-surface text-2xs font-bold text-fg-tertiary uppercase">
              {selectable ? (
                <th scope="col" className="w-12 px-2.5 py-2 text-start">
                  <Checkbox
                    label={<span className="ady-sr-only">{t("table.selectAll")}</span>}
                    checked={allSelected}
                    onChange={() =>
                      onSelectedChange?.(allSelected ? new Set() : new Set(rows.map(rowId)))
                    }
                  />
                </th>
              ) : null}
              {columns.map((c) => (
                <th
                  key={c.id}
                  scope="col"
                  className={cx(
                    "px-2.5 py-2.5 whitespace-nowrap lg:py-2",
                    c.align === "end" || c.numeric ? "text-end" : "text-start",
                    hideClass(c),
                  )}
                >
                  {c.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading
              ? showSkeleton
                ? skeletonRows.map((i) => (
                    <tr key={i} className="border-t border-subtle">
                      {selectable ? <td className="px-2.5 py-3" /> : null}
                      {columns.map((c) => (
                        <td key={c.id} className={cx("px-2.5 py-3", hideClass(c))}>
                          <Skeleton className={c.numeric ? "ms-auto w-16" : "w-3/4"} />
                        </td>
                      ))}
                    </tr>
                  ))
                : null
              : rows.map((row) => {
                  const id = rowId(row);
                  return (
                    <tr
                      key={id}
                      className={cx(
                        "border-t border-subtle",
                        selected.has(id) ? "bg-selected" : "hover:bg-hover",
                        onRowOpen && "cursor-pointer",
                      )}
                      onClick={onRowOpen ? () => onRowOpen(row) : undefined}
                    >
                      {selectable ? (
                        <td className="px-2.5" onClick={(e) => e.stopPropagation()}>
                          <Checkbox
                            label={<span className="ady-sr-only">{t("table.selectRow")}</span>}
                            checked={selected.has(id)}
                            onChange={() => toggle(id)}
                          />
                        </td>
                      ) : null}
                      {columns.map((c) => (
                        <td
                          key={c.id}
                          data-content={c.userContent ? "user" : undefined}
                          className={cx(
                            dense ? "px-2.5 py-2 text-xs" : "px-2.5 py-3 text-sm lg:py-2",
                            (c.align === "end" || c.numeric) && "text-end",
                            c.numeric && "ady-ledger",
                            hideClass(c),
                          )}
                        >
                          {c.cell(row)}
                        </td>
                      ))}
                    </tr>
                  );
                })}
          </tbody>
        </table>
      </div>

      {/* Narrow container: one card per row. */}
      <div className="ady-table-cards">
        <ul className="flex flex-col gap-2" aria-label={caption}>
          {loading
            ? showSkeleton
              ? skeletonRows.map((i) => (
                  <li key={i} className="rounded-md border border-subtle bg-surface p-4">
                    <Skeleton className="mb-3 w-1/2" />
                    <Skeleton className="w-3/4" />
                  </li>
                ))
              : null
            : rows.map((row) => {
                const id = rowId(row);
                const body = (
                  <>
                    <span className="flex items-start justify-between gap-3">
                      <span className="text-lg font-medium" data-content="user">
                        {card.title(row)}
                      </span>
                      {card.amount ? (
                        <span className="ady-ledger text-lg">{card.amount(row)}</span>
                      ) : null}
                    </span>
                    {card.status ? (
                      <span className="flex flex-wrap gap-2">{card.status(row)}</span>
                    ) : null}
                    {card.details ? (
                      <span className="text-sm text-fg-secondary">{card.details(row)}</span>
                    ) : null}
                    {card.extra && !onRowOpen ? card.extra(row) : null}
                  </>
                );
                return (
                  <li
                    key={id}
                    className={cx(
                      "flex items-start gap-1 rounded-md border border-subtle bg-surface",
                      selected.has(id) && "bg-selected",
                    )}
                  >
                    {selectable ? (
                      <span className="ps-4 pt-2">
                        <Checkbox
                          label={<span className="ady-sr-only">{t("table.selectRow")}</span>}
                          checked={selected.has(id)}
                          onChange={() => toggle(id)}
                        />
                      </span>
                    ) : null}
                    {onRowOpen ? (
                      <button
                        type="button"
                        className="flex min-h-11 flex-1 flex-col gap-2 p-4 text-start"
                        onClick={() => onRowOpen(row)}
                      >
                        {body}
                      </button>
                    ) : (
                      <div className="flex flex-1 flex-col gap-2 p-4">{body}</div>
                    )}
                    {/* Beside the open button, never inside it (buttons can't nest). */}
                    {card.extra && onRowOpen ? (
                      <span className="flex-none pe-2 pt-3">{card.extra(row)}</span>
                    ) : null}
                  </li>
                );
              })}
        </ul>
      </div>

      {selectable && selected.size > 0 ? (
        <p className="ady-sr-only" role="status">
          {t("table.selectedCount", { count: selected.size })}
        </p>
      ) : null}
    </div>
  );
}
